import type { WidgetCategoryView, WidgetSchema } from '../types';
import { advancedChart } from './advanced-chart';
import { computeForwardCurve } from './compute-forward-curve';
import { kpow } from './kpow';
import { kpowBroadcast } from './kpow-broadcast';
import { marketHeatmap } from './market-heatmap';
import { midtermsMap } from './midterms-map';
import { symbolDetail } from './symbol-detail';
import { tickerTape } from './ticker-tape';
import { watchlist } from './watchlist';

/**
 * Source of truth for which widgets run on the new config engine. Each entry is
 * added here as it is migrated (Steps 2-7). The `[item]` page renders the
 * legacy preview path for any id NOT present here, so migration is incremental
 * and nothing breaks mid-way.
 */
const ENGINE_WIDGETS: WidgetSchema[] = [
    advancedChart,
    symbolDetail,
    watchlist,
    tickerTape,
    kpow,
    kpowBroadcast,
    computeForwardCurve,
    midtermsMap,
    marketHeatmap,
];

export const WIDGET_REGISTRY: Record<string, WidgetSchema> = Object.fromEntries(
    ENGINE_WIDGETS.map((widget) => [widget.meta.id, widget]),
);

export function getWidget(id: string): WidgetSchema | undefined {
    return WIDGET_REGISTRY[id];
}

/** Group registry widgets by category, preserving first-seen order. */
export function listCategories(): WidgetCategoryView[] {
    const order: string[] = [];
    const byCategory = new Map<string, WidgetCategoryView>();
    for (const widget of ENGINE_WIDGETS) {
        let view = byCategory.get(widget.meta.category);
        if (!view) {
            view = { slug: widget.meta.category, title: widget.meta.categoryTitle, widgets: [] };
            byCategory.set(widget.meta.category, view);
            order.push(widget.meta.category);
        }
        view.widgets.push(widget);
    }
    return order.map((category) => byCategory.get(category)!);
}

/** Categories for the sidebar/gallery, excluding hidden widgets + empty categories. */
export function sidebarCategories(): WidgetCategoryView[] {
    return listCategories()
        .map((category) => ({ ...category, widgets: category.widgets.filter((w) => !w.meta.hideFromSidebar) }))
        .filter((category) => category.widgets.length > 0);
}

/** Route params for `generateStaticParams` on the [category]/[item] page. */
export function allRouteParams(): { category: string; item: string }[] {
    return ENGINE_WIDGETS.map((widget) => ({ category: widget.meta.category, item: widget.meta.id }));
}
